import { useEffect } from "react";
import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { ErrorFallback } from "~/components/errors/error-fallback-screen";
import { AnalyticsContextProvider } from "~/contexts/analytics-context";
import { useAuth, type IAuthContext } from "~/contexts/auth-context";
import { useFeatureFlags } from "~/contexts/feature-flag-context";
import { useNetworkStatus } from "~/hooks/use-network-status";
import { MaintenancePage } from "~/pages/maintenance/maintenance-page";
import { NotFound } from "~/pages/not-found";
import { logError } from "~/shared/services/error-reporting";
import { toast } from "~/utils/toast";
import { AppLayout } from "~/components/layouts/app-layout";
import { HeadContent } from "~/components/head-content";

export const Route = createRootRouteWithContext<{ auth: IAuthContext }>()({
  component: RootLayout,
  errorComponent: RootError,
  notFoundComponent: RootNotFound,
});

function RootLayout() {
  const { t } = useTranslation(["general"]);
  const { isOnline } = useNetworkStatus();
  const { features } = useFeatureFlags();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!isOnline) {
      toast.error(t("general:errorNetworkNoInternetConnection"));
    }
  }, [isOnline, t]);

  return (
    <>
      <HeadContent />
      <AnalyticsContextProvider>
        {features.maintenanceMode && pathname !== "/contact-us" ? (
          <MaintenancePage />
        ) : (
          <Outlet />
        )}
      </AnalyticsContextProvider>
      <TanStackDevtools
        plugins={[
          { name: "TanStack Query", render: <ReactQueryDevtoolsPanel /> },
          { name: "TanStack Router", render: <TanStackRouterDevtoolsPanel /> },
        ]}
      />
    </>
  );
}

// Unknown top-level paths resolve here rather than under /_app, so a signed-in
// user still gets the app shell around the page.
function RootNotFound() {
  const { session } = useAuth();

  if (!session) {
    return <NotFound />;
  }

  return (
    <AppLayout>
      <NotFound />
    </AppLayout>
  );
}

function RootError({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    logError(error);
  }, [error]);

  return (
    <ErrorFallback
      error={error}
      componentStack=""
      eventId=""
      resetError={reset}
    />
  );
}
